/**
 * The hold between a room landing and the screen opening on it.
 *
 * A room arrives in pieces. The tilemap comes first, in the world state that announces the room;
 * the entities standing in it follow a tick or two later; and a character is not DRAWABLE even
 * then -- an LPC body is composited from spritesheets that are fetched on demand (see
 * SpriteSheetLoader) and baked after they land. Measured in the browser on a character-creation
 * drop into an ember camp: the map was painted 19ms after the room message, and the camp's lord
 * only had a sprite 510ms after that. The screen used to start opening the instant the tilemap
 * arrived, so the half second the rest of the camp took to assemble was spent in plain sight --
 * bare ground, then bodies popping in one by one, over an already-lifting curtain.
 *
 * So: when the room lands, hold the curtain and ask every frame whether the scene is worth looking
 * at yet. The answer is yes when the map has been painted, nothing the renderer is SHOWING is still
 * waiting on a sprite, and no new entity has arrived for a moment (the stragglers of a room load
 * come in a burst, and the first of them must not be mistaken for the last).
 *
 * The cap is what makes this safe. Everything above can fail to settle -- a sheet that 404s and
 * retries, a server that goes quiet mid-load, a camp that keeps spawning -- and a curtain nothing
 * lifts is a worse bug than the pop-in it was closed against. At MAX_HOLD_MS the screen opens on
 * whatever is there.
 */
export interface SceneReadiness {
    /** The map still owes the screen a paint. */
    mapPending: boolean
    /** Entities currently on screen that cannot draw themselves yet (sheets in flight, or baking). */
    spritesPending: number
}

export class RoomReveal {
    /**
     * The longest the screen is held black waiting for a room to finish arriving. Generous against
     * the measured ~0.5s (a slow link fetching a dozen sheets is the case that needs the room),
     * short enough that a hold nothing ever satisfies still reads as a load rather than a crash.
     */
    static readonly MAX_HOLD_MS = 1200

    /**
     * How long the scene has to stand still before it counts as complete. A room's entities arrive
     * over a few server ticks, so "nothing on screen is waiting on a sprite" is true -- briefly and
     * meaninglessly -- in the gap before the first of them lands.
     */
    static readonly SETTLE_MS = 120

    private armedTs?: number
    private lastArrivalTs: number = 0

    /** A room has landed: close the curtain on it until the scene is ready to be looked at. */
    arm = (now: number) => {
        this.armedTs = now
        this.lastArrivalTs = now
    }

    /** Something new turned up in the room. Resets the settle window, never starts one. */
    noteArrival = (now: number) => {
        if (this.armedTs === undefined) {
            return
        }
        this.lastArrivalTs = now
    }

    isHolding = (): boolean => this.armedTs !== undefined

    /** Abandon the hold without opening the screen (a second room load overtakes the first). */
    cancel = () => {
        this.armedTs = undefined
    }

    /**
     * Should the screen open now? Disarms the hold when it answers yes, so the caller can drive this
     * straight from the frame loop and fade in on the one frame it returns true.
     */
    shouldReveal = (now: number, scene: SceneReadiness): boolean => {
        if (this.armedTs === undefined) {
            return false
        }

        // The cap: whatever the scene is doing, this is as long as the screen stays shut.
        if (now - this.armedTs >= RoomReveal.MAX_HOLD_MS) {
            this.armedTs = undefined
            return true
        }

        if (scene.mapPending || scene.spritesPending > 0) {
            return false
        }

        if (now - this.lastArrivalTs < RoomReveal.SETTLE_MS) {
            return false
        }

        this.armedTs = undefined
        return true
    }
}
